"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { DeskBackButton } from "@/components/DeskBackButton";
import { ExpiryDeskWordmark } from "@/components/ExpiryDeskWordmark";
import { supabase } from "@/lib/supabaseClient";
import { fetchMedicationsForHistory } from "@/lib/fetchMedicationsForHistory";
import { resolveExpiryDate } from "@/lib/resolveExpiryDate";

type MedRow = {
  id: string | number;
  name: string;
  dosage: string | null;
  quantity: number;
  unit: string;
  expiry_month: number;
  expiry_year: number;
  price_per_box: number | null;
  status: string | null;
  scan_date: string | null;
  created_at: string | null;
};

type ScanGroup = {
  key: string;
  scanDate: string;
  items: MedRow[];
  itemsCount: number;
  wasteValue: number;
  atRiskValue: number;
  savedValue: number;
};

type AuthSession = {
  user: { id: string };
} | null;

function formatCurrency(val: number) {
  return (
    "R " +
    Number(val).toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function formatScanDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
}

function daysUntilExpiry(month: number, year: number) {
  const today = new Date();
  const expiry = resolveExpiryDate(month, year, "last");
  return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getStatusChipStyle(status: string | null) {
  if (status === "item_expired") {
    return { label: "expired", bg: "var(--ed-ui-critical-bg)", border: "var(--ed-ui-critical-border)", text: "var(--ed-ui-critical)" };
  }
  if (status === "item_moved") {
    return { label: "moved", bg: "var(--ed-ui-info-bg)", border: "var(--ed-ui-info-border)", text: "var(--ed-ui-info-strong)" };
  }
  return { label: "active", bg: "var(--ed-ui-safe-bg)", border: "var(--ed-ui-safe-border)", text: "var(--ed-ui-safe)" };
}

function getExpiryUrgencyChip(month: number, year: number, status: string | null) {
  if (status === "item_expired") {
    return { label: "—", bg: "var(--ed-glass-bg)", border: "var(--ed-border)", text: "var(--ed-text-muted)" };
  }
  if (status === "item_moved") {
    return { label: "—", bg: "var(--ed-ui-info-bg)", border: "var(--ed-ui-info-border)", text: "var(--ed-ui-info-strong)" };
  }
  const days = daysUntilExpiry(Number(month || 0), Number(year || 0));
  if (days < 0) {
    return { label: "OVERDUE", bg: "var(--ed-ui-critical-bg)", border: "var(--ed-ui-critical-border)", text: "var(--ed-ui-critical)" };
  }
  if (days <= 30) {
    return { label: "CRITICAL", bg: "var(--ed-ui-warning-bg)", border: "var(--ed-ui-warning-border)", text: "var(--ed-ui-warning)" };
  }
  if (days <= 90) {
    return { label: "AT RISK", bg: "var(--ed-ui-warning-bg)", border: "var(--ed-ui-warning-border)", text: "var(--ed-ui-warning-strong)" };
  }
  return { label: "SAFE", bg: "var(--ed-ui-safe-bg)", border: "var(--ed-ui-safe-border)", text: "var(--ed-ui-safe)" };
}

function HistoryVisionEmpty() {
  return (
    <div className="ed-history-vision">
      <p className="ed-history-vision__eyebrow">Coming into focus</p>
      <h2 className="ed-history-vision__headline">Your inventory command thread — built for what&apos;s next</h2>
      <p className="ed-history-vision__copy">
        History &amp; scans will become the place where ExpiryDesk pulls together sheet uploads, dashboard activity, and
        everything else you do in the app — then turns it into clear, daily insights. On the top tier, you&apos;ll wake
        up to a fresh read on risk, waste, and what to do first.
      </p>
      <div className="ed-history-vision__grid">
        <div className="ed-history-vision-card">
          <div className="ed-history-vision-card__icon" aria-hidden>
            📊
          </div>
          <h3 className="ed-history-vision-card__title">One thread for every scan</h3>
          <p className="ed-history-vision-card__text">
            Names grouped by category and status, with the same honest copy you already rely on — so you can scan the
            past at a glance before you act on the present.
          </p>
        </div>
        <div className="ed-history-vision-card">
          <div className="ed-history-vision-card__icon" aria-hidden>
            ☁️
          </div>
          <h3 className="ed-history-vision-card__title">Photos in the cloud, not on your phone</h3>
          <p className="ed-history-vision-card__text">
            Full-resolution captures stay in secure storage so we save your device space. When downloads roll out,
            you&apos;ll pull exactly what the camera saw — per line or whole batch.
          </p>
        </div>
        <div className="ed-history-vision-card">
          <div className="ed-history-vision-card__icon" aria-hidden>
            ✨
          </div>
          <h3 className="ed-history-vision-card__title">Insights that stitch the app together</h3>
          <p className="ed-history-vision-card__text">
            We&apos;re wiring this hub to absorb context from the dashboard and tools so Premium members get proactive,
            plain-language guidance every single day — not just a static list.
          </p>
        </div>
        <div className="ed-history-vision-card">
          <div className="ed-history-vision-card__icon" aria-hidden>
            🧭
          </div>
          <h3 className="ed-history-vision-card__title">Start on the desk today</h3>
          <p className="ed-history-vision-card__text">
            Upload a sheet or add a line — we date each run automatically. When History fills in, you&apos;ll already
            have the habits that make the future feed worthwhile.
          </p>
        </div>
      </div>
      <p className="ed-history-vision__footnote">
        Nothing dated yet? That&apos;s expected early on. Keep capturing stock — this page will light up as your scan
        trail grows.
      </p>
      <div className="ed-history-vision__actions">
        <Link href="/dashboard" className="ed-btn-primary" style={{ textDecoration: "none", padding: "0.65rem 1.25rem" }}>
          Open dashboard
        </Link>
      </div>
    </div>
  );
}

export default function HistoryPage() {
  const [rows, setRows] = useState<MedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [session, setSession] = useState<AuthSession>(null);
  const [viewportWidth, setViewportWidth] = useState(1200);

  const supabaseConfigured = Boolean(supabase);
  const isMobile = viewportWidth < 768;

  useEffect(() => {
    function onResize() {
      if (typeof window !== "undefined") setViewportWidth(window.innerWidth);
    }
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      const client = supabase;
      if (!client) {
        setAuthLoading(false);
        return;
      }

      const { data } = await client.auth.getSession();
      if (!mounted) return;
      setSession(data.session);
      setAuthLoading(false);

      const { data: sub } = client.auth.onAuthStateChange((_event, newSession) => {
        setSession(newSession);
      });

      return () => {
        sub.subscription.unsubscribe();
      };
    }

    const cleanupPromise = initAuth();
    return () => {
      mounted = false;
      void Promise.resolve(cleanupPromise).then((cleanup) => {
        if (typeof cleanup === "function") cleanup();
      });
    };
  }, []);

  useEffect(() => {
    async function loadHistory() {
      setLoading(true);
      setError(null);

      const sb = supabase;
      if (!sb || !session?.user?.id) {
        setRows([]);
        setLoading(false);
        return;
      }

      const { data, error: queryError } = await fetchMedicationsForHistory(sb, session.user.id);

      if (queryError) {
        setError("We couldn't load your history. Try refreshing the page.");
        setLoading(false);
        return;
      }

      setRows((data || []) as MedRow[]);
      setLoading(false);
    }

    loadHistory();
  }, [session?.user?.id]);

  const scans = useMemo<ScanGroup[]>(() => {
    const map = new Map<string, ScanGroup>();

    for (const row of rows) {
      if (!row.scan_date) continue;
      const scanDate = row.scan_date;
      if (!map.has(scanDate)) {
        map.set(scanDate, {
          key: scanDate,
          scanDate,
          items: [],
          itemsCount: 0,
          wasteValue: 0,
          atRiskValue: 0,
          savedValue: 0,
        });
      }

      const group = map.get(scanDate)!;
      group.items.push(row);
      group.itemsCount += 1;

      const quantity = Number(row.quantity || 0);
      const price = Number(row.price_per_box || 0);
      const value = quantity * price;
      if (row.status === "item_expired") {
        group.wasteValue += value;
      } else if (row.status === "item_moved") {
        group.savedValue += value;
      } else {
        const days = daysUntilExpiry(Number(row.expiry_month || 0), Number(row.expiry_year || 0));
        if (days <= 90) group.atRiskValue += value;
      }
    }

    return Array.from(map.values()).sort(
      (a, b) => new Date(b.scanDate).getTime() - new Date(a.scanDate).getTime(),
    );
  }, [rows]);

  const totals = useMemo(() => {
    return scans.reduce(
      (acc, s) => {
        acc.waste += s.wasteValue;
        acc.atRisk += s.atRiskValue;
        acc.saved += s.savedValue;
        acc.lines += s.itemsCount;
        return acc;
      },
      { waste: 0, atRisk: 0, saved: 0, lines: 0 },
    );
  }, [scans]);

  const didAutoExpand = useRef(false);
  useEffect(() => {
    if (scans.length === 0) {
      didAutoExpand.current = false;
      queueMicrotask(() => {
        setExpanded(null);
      });
      return;
    }
    if (!didAutoExpand.current) {
      didAutoExpand.current = true;
      queueMicrotask(() => {
        setExpanded(scans[0]!.key);
      });
    }
  }, [scans]);

  const showMainChrome = supabaseConfigured && !authLoading && session;

  const font = "var(--font-sans)";
  const text = "var(--ed-text)";
  const textSecondary = "var(--ed-text-secondary)";
  const textMuted = "var(--ed-text-muted)";

  return (
    <div id="main" tabIndex={-1} className="ed-history-shell">
      <div className="ed-history-shell__aurora" aria-hidden />
      <div className="ed-history-inner">
        <header className="ed-history-header">
          <div className="ed-history-header__bar">
            <DeskBackButton />
            <div className="ed-history-header__brand">
              <ExpiryDeskWordmark variant="nav" showTagline={false} href="/dashboard" />
            </div>
          </div>
          <h1 className="ed-history-header__title">History &amp; scans</h1>
          <p className="ed-history-header__lead">
            Each sheet upload and <strong>+ Add line</strong> is dated automatically. Expand a batch to review lines,
            or copy details before you adjust something on the dashboard.
          </p>
        </header>

        {!supabaseConfigured && (
          <div className="ed-card-glass" style={{ padding: "1.25rem", color: textSecondary }}>
            Supabase is not configured. Add <code>NEXT_PUBLIC_SUPABASE_URL</code> and{" "}
            <code>NEXT_PUBLIC_SUPABASE_ANON_KEY</code> to use history.
          </div>
        )}

        {supabaseConfigured && authLoading && (
          <div className="ed-card-glass" style={{ padding: "1.25rem", color: textSecondary }}>
            Loading session…
          </div>
        )}

        {supabaseConfigured && !authLoading && !session && (
          <div className="ed-card-glass" style={{ padding: "2rem 1.5rem", textAlign: "center" }}>
            <p style={{ color: textSecondary, margin: "0 0 1.25rem", fontSize: "0.95rem", lineHeight: 1.55 }}>
              Sign in to see your History &amp; scans.
            </p>
            <Link href="/dashboard" className="ed-btn-primary" style={{ display: "inline-block", textDecoration: "none", padding: "0.65rem 1.35rem", fontFamily: font }}>
              Sign in
            </Link>
          </div>
        )}

        {showMainChrome && loading && (
          <div className="ed-card-glass" style={{ padding: "1.25rem", color: textSecondary }}>
            Loading…
          </div>
        )}

        {showMainChrome && error && (
          <div
            style={{
              background: "var(--ed-ui-critical-bg)",
              border: "1px solid var(--ed-ui-critical-border)",
              borderRadius: "var(--ed-radius-sm)",
              padding: "1.25rem",
              color: "var(--ed-ui-critical)",
            }}
          >
            <p style={{ margin: "0 0 0.75rem", lineHeight: 1.5 }}>{error}</p>
            {/deleted_at/i.test(error) ? (
              <p style={{ margin: 0, fontSize: "0.85rem", opacity: 0.92, lineHeight: 1.55 }}>
                Your database may need the latest migration. In Supabase → SQL Editor, run the{" "}
                <code style={{ wordBreak: "break-all" }}>deleted_at</code> lines from{" "}
                <code>supabase/medications_schema.sql</code> (adds soft-delete support), then reload.
              </p>
            ) : null}
          </div>
        )}

        {showMainChrome && !loading && !error && scans.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            {[
              {
                label: "Uploads",
                value: String(scans.length),
                sub: `${totals.lines} lines`,
                color: "var(--ed-ui-info-strong)",
              },
              {
                label: "Write-offs",
                value: formatCurrency(totals.waste),
                sub: "Expired in these batches",
                color: "var(--ed-ui-critical-strong)",
              },
              {
                label: "At risk",
                value: formatCurrency(totals.atRisk),
                sub: `Recovered ${formatCurrency(totals.saved)}`,
                color: "var(--ed-ui-warning-strong)",
              },
            ].map((k) => (
              <div key={k.label} className="ed-card-glass" style={{ padding: "1.2rem 1.3rem" }}>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    color: textMuted,
                    marginBottom: "0.45rem",
                    textTransform: "uppercase",
                  }}
                >
                  {k.label}
                </div>
                <div style={{ fontSize: "1.45rem", color: k.color, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em" }}>{k.value}</div>
                <div style={{ color: textSecondary, fontSize: "0.8125rem", marginTop: "0.4rem", lineHeight: 1.4 }}>{k.sub}</div>
              </div>
            ))}
          </div>
        )}

        {showMainChrome && !loading && !error && scans.length === 0 && <HistoryVisionEmpty />}

        {showMainChrome && !loading && !error && scans.length > 0 && (
          <div style={{ display: "grid", gap: "0.9rem" }}>
            {scans.map((scan) => {
              const isOpen = expanded === scan.key;
              return (
                <div key={scan.key} className="ed-card-glass" style={{ overflow: "hidden", padding: 0 }}>
                  <button
                    type="button"
                    className="ed-history-scan-head"
                    onClick={() => setExpanded(isOpen ? null : scan.key)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      background: "transparent",
                      border: "none",
                      borderBottom: isOpen ? "1px solid var(--ed-border)" : "none",
                      cursor: "pointer",
                      padding: "1rem 1.1rem",
                      fontFamily: font,
                      transition: "background 0.2s var(--ed-ease)",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gap: "0.45rem",
                        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            color: textMuted,
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                          }}
                        >
                          Date
                        </div>
                        <div style={{ color: text, fontWeight: 600 }}>{formatScanDate(scan.scanDate)}</div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            color: textMuted,
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                          }}
                        >
                          Items
                        </div>
                        <div style={{ color: text, fontWeight: 600 }}>{scan.itemsCount}</div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            color: textMuted,
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                          }}
                        >
                          Write-off
                        </div>
                        <div style={{ color: "var(--ed-ui-critical)", fontWeight: 700 }}>{formatCurrency(scan.wasteValue)}</div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            color: textMuted,
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                          }}
                        >
                          At risk
                        </div>
                        <div style={{ color: "var(--ed-ui-warning)", fontWeight: 700 }}>{formatCurrency(scan.atRiskValue)}</div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            color: textMuted,
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                          }}
                        >
                          Recovered
                        </div>
                        <div style={{ color: "var(--ed-ui-safe)", fontWeight: 700 }}>{formatCurrency(scan.savedValue)}</div>
                      </div>
                      <div style={{ justifySelf: "end", color: textMuted, fontWeight: 600, fontSize: "0.8125rem" }}>{isOpen ? "Hide" : "Details"}</div>
                    </div>
                  </button>

                  {isOpen && (
                    <div style={{ padding: "0.9rem 1.1rem 1.05rem" }}>
                      <div
                        className="ed-history-lines-scroll"
                        style={{ overflowX: "auto", border: "1px solid var(--ed-border)", borderRadius: "var(--ed-radius-sm)" }}
                      >
                        <table className="ed-history-lines-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ background: "var(--ed-ui-info-bg)", color: textMuted, fontSize: "0.72rem", fontWeight: 600 }}>
                              <th style={{ textAlign: "left", padding: "0.65rem" }}>Name</th>
                              <th style={{ textAlign: "left", padding: "0.65rem" }}>Strength</th>
                              <th style={{ textAlign: "left", padding: "0.65rem" }}>Qty</th>
                              <th style={{ textAlign: "left", padding: "0.65rem" }}>Expiry</th>
                              <th style={{ textAlign: "left", padding: "0.65rem" }}>Expiry window</th>
                              <th style={{ textAlign: "left", padding: "0.65rem" }}>Status</th>
                              <th style={{ textAlign: "right", padding: "0.65rem" }}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scan.items.map((m) => {
                              const totalValue = Number(m.quantity || 0) * Number(m.price_per_box || 0);
                              const statusChip = getStatusChipStyle(m.status);
                              const urg = getExpiryUrgencyChip(
                                Number(m.expiry_month || 0),
                                Number(m.expiry_year || 0),
                                m.status,
                              );
                              return (
                                <tr key={String(m.id)} style={{ borderTop: "1px solid rgba(226, 232, 240, 0.9)" }}>
                                  <td style={{ padding: "0.65rem", color: text, fontWeight: 600 }}>{m.name}</td>
                                  <td style={{ padding: "0.65rem", color: textSecondary }}>{m.dosage || "—"}</td>
                                  <td style={{ padding: "0.65rem", color: textSecondary }}>
                                    {m.quantity} {m.unit}
                                  </td>
                                  <td style={{ padding: "0.65rem", color: textSecondary }}>
                                    {String(m.expiry_month || "").padStart(2, "0")}/{m.expiry_year}
                                  </td>
                                  <td style={{ padding: "0.65rem" }}>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "0.22rem 0.65rem",
                                        borderRadius: "9999px",
                                        border: `1px solid ${urg.border}`,
                                        background: urg.bg,
                                        color: urg.text,
                                        fontFamily: font,
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.04em",
                                      }}
                                    >
                                      {urg.label}
                                    </span>
                                  </td>
                                  <td style={{ padding: "0.65rem" }}>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "0.22rem 0.65rem",
                                        borderRadius: "9999px",
                                        border: `1px solid ${statusChip.border}`,
                                        background: statusChip.bg,
                                        color: statusChip.text,
                                        fontFamily: font,
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {statusChip.label}
                                    </span>
                                  </td>
                                  <td style={{ padding: "0.65rem", color: text, textAlign: "right", fontWeight: 600 }}>
                                    {formatCurrency(totalValue)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
